import React, { useState, useEffect, useMemo, useRef } from 'react';
import { getAvailability } from '../utils/api';
import { useCalendar } from '../contexts/CalendarContext';
import { useSession } from '../contexts/SessionContext';
import { useBranding } from '../contexts/BrandingContext';
import { IconButton, CircularProgress, Box } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { formatServiceDuration, formatServicePrice, STARTING_AT_LABEL } from '../utils/serviceDisplay';
import {
  availabilityKey,
  isNoSharedVisitTypeError,
  offeredVisitTypes,
  slotsFor,
  DEFAULT_PICKUP_PROMISE,
  NO_SHARED_VISIT_TYPE_MESSAGE,
  VISIT_TYPE_DROP_OFF,
} from '../utils/visitType';

import { STEPS, recordStepDetail, trackNoAvailabilityShown } from '../analytics';

// Group slots by date and sort them chronologically
const groupSlotsByDate = (slots) => {
  const grouped = {};
  slots.forEach(slot => {
    // Extract date directly from the string (YYYY-MM-DD format)
    // The API sends times like "2025-11-04T17:00:00" which are local times
    const dateStr = slot.split('T')[0];
    if (!grouped[dateStr]) {
      grouped[dateStr] = [];
    }
    grouped[dateStr].push(slot);
  });

  // Sort the slots for each date chronologically
  Object.keys(grouped).forEach(dateStr => {
    grouped[dateStr].sort((a, b) => new Date(a) - new Date(b));
  });

  return grouped;
};

const DateSelection = ({ onChangeServices }) => {
  const { calendarId, enableWaiterDropoff } = useCalendar();
  const { branding } = useBranding();
  const { 
    sessionId, 
    selectedLocation, 
    selectedServices, 
    selectedTimeSlot, 
    setSelectedTimeSlot,
    // CIN-7181 (all absent -> feature off behaviour)
    setCurrentStep,
    visitType,
    setVisitType,
    setVisitTypeOffered,
    setVisitTypeStepActive,
    setVisitTypeError,
    availabilityPrefetch,
    setAvailabilityPrefetch,
  } = useSession();
  
  const locationId = selectedLocation?.id;
  const serviceIds = useMemo(() => selectedServices.map(s => s.id), [selectedServices]);
  // CIN-7181: the whole availability response is kept so the slot list can be
  // read for the chosen visit type (slots_by_visit_type) when the feature is
  // on, or from available_slots exactly as today when it is off. This is the
  // ONLY place Date & Time reads slots (one fetch; weeks page client-side).
  const [availabilityResponse, setAvailabilityResponse] = useState(null);
  const allSlots = useMemo(
    () => (availabilityResponse ? slotsFor(availabilityResponse, enableWaiterDropoff, visitType) : []),
    [availabilityResponse, enableWaiterDropoff, visitType]
  );
  const availabilityData = useMemo(() => groupSlotsByDate(allSlots), [allSlots]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentWeekStart, setCurrentWeekStart] = useState(null);
  const dayStripRef = useRef(null);

  // CIN-7080: how far the consumer had to page before booking says whether the
  // calendar's near-term availability matches what people are looking for.
  const weeksPagedRef = useRef(0);

  // CIN-7080: reported from the fetch itself, not from an empty `allSlots`.
  // The initial-load effect clears `loading` without fetching whenever a
  // prerequisite is still missing, so "no slots in state" means "we have not
  // asked yet" just as often as it means "there are none". Keyed by location
  // and services, because the same screen re-asks when either changes.
  const emptyReportedForRef = useRef(null);
  const noteWeekPaged = () => {
    weeksPagedRef.current += 1;
    recordStepDetail(STEPS.DATE_TIME, { weeks_paged: weeksPagedRef.current });
  };

  // Get the start of the week (Sunday) for a given date
  const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day;
    const weekStart = new Date(d.setDate(diff));
    weekStart.setHours(0, 0, 0, 0);
    return weekStart;
  };


  // Generate dates for the current week
  const getCurrentWeekDates = () => {
    if (!currentWeekStart) return [];

    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(currentWeekStart);
      date.setDate(currentWeekStart.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const currentWeekDates = getCurrentWeekDates();

  // Format date for API call (YYYY-MM-DD)
  const formatDateForAPI = (date) => {
    return date.toISOString().split('T')[0];
  };

  // Navigation functions
  const goToPreviousWeek = () => {
    const newWeekStart = new Date(currentWeekStart);
    newWeekStart.setDate(currentWeekStart.getDate() - 7);

    // Don't go before today's week
    const today = new Date();
    const todayWeekStart = getWeekStart(today);

    if (newWeekStart >= todayWeekStart) {
      noteWeekPaged();
      setCurrentWeekStart(newWeekStart);
    }
  };

  const goToNextWeek = () => {
    const newWeekStart = new Date(currentWeekStart);
    newWeekStart.setDate(currentWeekStart.getDate() + 7);
    noteWeekPaged();
    setCurrentWeekStart(newWeekStart);
  };

  // Check if we can go to previous week
  const canGoPrevious = () => {
    if (!currentWeekStart) return false;
    const today = new Date();
    const todayWeekStart = getWeekStart(today);
    return currentWeekStart > todayWeekStart;
  };

  // Format time slot for display (9:00 AM)
  const formatTimeSlot = (timeSlot) => {
    const date = new Date(timeSlot);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // CIN-7181 guard (feature on): every path that lands here without passing
  // Services Next - a `?service=` / `~` deep link, a cookie restore saved
  // before the flag was on, a CIN-7133 replacing link - may carry no visit type
  // (or one the selection no longer offers). Resolve it from THIS response:
  // one offered -> preselect it; both -> send the visitor to the choice step
  // rather than silently showing one type's times; none -> the existing
  // no-availability copy.
  const applyAvailability = (response) => {
    setAvailabilityResponse(response);
    if (!enableWaiterDropoff) return;
    const offered = offeredVisitTypes(response);
    if (offered.includes(visitType)) return;
    // Past this point the held visit type is missing or no longer offered, so
    // any held slot was not picked on the grid of the type that will be used:
    // keeping it would PATCH a wrong drop-off bay claim and /book a waiter at
    // a drop-off time (or vice versa). Every branch below drops it. Not
    // gated on this render's selectedTimeSlot: a cookie restore can land the
    // slot while this fetch is in flight, after the closure was captured.
    setSelectedTimeSlot(null);
    setVisitTypeOffered(offered);
    if (offered.length === 1) {
      setVisitType(offered[0]);
    } else if (offered.length === 2) {
      setVisitTypeStepActive(true);
      setCurrentStep(2);
    } else if (visitType !== null) {
      setVisitType(null);
    }
  };

  // Fetch all availability at once (no date filter)
  const fetchAllAvailability = async () => {
    if (!calendarId || !sessionId || !locationId || !serviceIds?.length) {
      setLoading(false);
      return;
    }

    // CIN-7181: reuse the response Services Next just fetched for this exact
    // location + services (consumed once, so a Back/Next refetches).
    if (availabilityPrefetch && availabilityPrefetch.key === availabilityKey(locationId, serviceIds)) {
      setAvailabilityPrefetch(null);
      setError(null);
      applyAvailability(availabilityPrefetch.response);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getAvailability(
        calendarId,
        sessionId,
        locationId,
        serviceIds
        // No date parameter - fetch all available slots
      );

      applyAvailability(response);

      // CIN-7080: report an empty result once per location + services. With
      // drop-offs on, empty means no offered visit type has a slot.
      const noSlots = enableWaiterDropoff
        ? offeredVisitTypes(response).every((type) => slotsFor(response, true, type).length === 0)
        : slotsFor(response, false).length === 0;
      const combination = `${locationId}:${serviceIds.join(',')}`;
      if (noSlots && emptyReportedForRef.current !== combination) {
        emptyReportedForRef.current = combination;
        trackNoAvailabilityShown({ calendarLocationId: locationId, serviceIds });
      }
    } catch (err) {
      if (enableWaiterDropoff && isNoSharedVisitTypeError(err)) {
        // CIN-7181: the services share no visit type - back to Services with
        // the inline message instead of a dead end here.
        setVisitTypeError(NO_SHARED_VISIT_TYPE_MESSAGE);
        setCurrentStep(2);
        return;
      }
      setError('Failed to load availability');
      console.error('Error fetching availability:', err);
    } finally {
      setLoading(false);
    }
  };

  // Auto-select the date with the earliest (most recent) available slot
  const autoSelectEarliestAvailableDate = () => {
    if (allSlots.length === 0) return;

    // Find the earliest slot
    const earliestSlot = allSlots.reduce((earliest, current) => {
      const currentDate = new Date(current);
      const earliestDate = new Date(earliest);
      return currentDate < earliestDate ? current : earliest;
    });

    // Extract the date from the earliest slot
    const earliestDate = new Date(earliestSlot);
    setSelectedDate(earliestDate);

    // Update current week to show the week containing the earliest slot
    const weekStart = getWeekStart(earliestDate);
    setCurrentWeekStart(weekStart);
  };

  // Initial load - fetch all availability
  useEffect(() => {
    if (!calendarId || !sessionId || !locationId || !serviceIds?.length) {
      setLoading(false);
      return;
    }

    fetchAllAvailability();
  }, [calendarId, sessionId, locationId, serviceIds]);

  // Auto-select earliest available date when availability data is loaded
  useEffect(() => {
    if (!selectedDate && allSlots.length > 0) {
      autoSelectEarliestAvailableDate();
    }
  }, [allSlots, selectedDate]);

  // CIN-7080: did the consumer take the soonest day the calendar had, or move
  // past it? Reported from the slots themselves rather than from the day strip,
  // which only shows one week at a time. Note this screen OPENS on the earliest
  // day, so `true` means the default was accepted, not that it was sought out.
  // Driven by state rather than the click handler so a resumed session, which
  // restores a slot without a click, is reported too.
  useEffect(() => {
    if (!selectedTimeSlot || allSlots.length === 0) return;
    const dayOf = (slot) => new Date(slot).toDateString();
    const earliest = allSlots.reduce((a, b) => (new Date(b) < new Date(a) ? b : a));
    recordStepDetail(STEPS.DATE_TIME, { is_earliest_day: dayOf(selectedTimeSlot) === dayOf(earliest) });
  }, [selectedTimeSlot, allSlots]);


  // The seven day buttons don't fit across a narrow phone (CIN-6404), so the strip
  // scrolls horizontally. Keep the selected day in view — otherwise a company whose
  // only availability is on Saturday auto-selects a day that sits off-screen.
  useEffect(() => {
    const strip = dayStripRef.current;
    if (!strip || !selectedDate) return;
    const selectedButton = strip.querySelector('[data-selected="true"]');
    if (!selectedButton) return;
    const stripBox = strip.getBoundingClientRect();
    const buttonBox = selectedButton.getBoundingClientRect();
    // Scroll the strip itself rather than using scrollIntoView, which would also
    // scroll the modal body.
    strip.scrollLeft += (buttonBox.left - stripBox.left) - (stripBox.width - buttonBox.width) / 2;
  }, [selectedDate, currentWeekStart]);

  const handleDateClick = (date, event) => {
    event.preventDefault();
    setSelectedDate(date);
  };

  const handleTimeSlotClick = (timeSlot, event) => {
    event.preventDefault();
    setSelectedTimeSlot(timeSlot);
  };

  // CIN-7133: selected-service summary (name, est. time, Starting At price).
  // Rendered as the FIRST child of .step-content in every return below so a
  // Reserve-with-Google landing shows the service while availability loads,
  // on error, and when the shop has no open slots. 'Change' is Scheduler's
  // handleBack (passed as onChangeServices), i.e. exactly the footer Back.
  const serviceSummary = selectedServices.length > 0 ? (
    <div className="selected-services-summary" data-testid="selected-services-summary">
      <div className="selected-services-summary-header">
        <span className="selected-services-summary-label">
          {selectedServices.length > 1 ? 'Selected services' : 'Selected service'}
        </span>
        {onChangeServices && (
          <button
            type="button"
            className="selected-services-summary-change"
            data-testid="selected-services-change"
            aria-label={selectedServices.length > 1 ? 'Change selected services' : 'Change selected service'}
            onClick={onChangeServices}
          >
            Change
          </button>
        )}
      </div>
      {selectedServices.map((s) => {
        const duration = formatServiceDuration(s.time_estimate);
        const priceText = formatServicePrice(s);
        return (
          <div key={s.id} className="selected-services-summary-item" data-testid="selected-services-summary-item">
            <span className="selected-services-summary-name">{s.name}</span>
            <span className="selected-services-summary-meta">
              {duration && <span data-testid="selected-services-summary-duration">Est. Time {duration}</span>}
              {priceText && (
                <span data-testid="selected-services-summary-price">{STARTING_AT_LABEL} {priceText}</span>
              )}
            </span>
          </div>
        );
      })}
    </div>
  ) : null;

  if (loading) {
    return (
      <div className="step-content">
        {serviceSummary}
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Loading available time slots...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="step-content">
        {serviceSummary}
        <div style={{ textAlign: 'center', padding: '40px', color: '#dc3545' }}>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // Check if there's no availability data
  if (allSlots.length === 0 && !loading) {
    return (
      <div className="step-content">
        {serviceSummary}
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p style={{ fontSize: '16px', color: '#666', marginBottom: '8px' }}>
            No appointments available for the selected location and services
          </p>
          <p style={{ fontSize: '14px', color: '#999' }}>
            Please try selecting different services or contact us for assistance
          </p>
        </div>
      </div>
    );
  }

  if (!currentWeekStart) {
    return (
      <div className="step-content">
        {serviceSummary}
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Initializing calendar...</p>
        </div>
      </div>
    );
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Helper function to convert hex to rgba with opacity
  const hexToRgba = (hex, opacity) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return `rgba(0, 123, 255, ${opacity})`; // fallback
    const r = parseInt(result[1], 16);
    const g = parseInt(result[2], 16);
    const b = parseInt(result[3], 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  const primaryColor = branding?.primary_color || '#007bff';
  const fadedPrimaryColor = hexToRgba(primaryColor, 0.1);

  return (
    <div className="step-content">
      {serviceSummary}
      {/* Week View */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 2, mb: 3 }}>
        <IconButton
          onClick={goToPreviousWeek}
          disabled={!canGoPrevious()}
          sx={{
            color: !canGoPrevious() ? '#ccc' : 'black',
          }}
        >
          <ChevronLeftIcon sx={{ fontSize: 24 }} />
        </IconButton>

        <Box
          ref={dayStripRef}
          sx={{
            display: 'flex',
            gap: 1,
            flex: 1,
            minWidth: 0,
            overflowX: 'auto',
            // `justify-content: center` would clip the first day out of reach once the
            // strip overflows, so centre via auto margins on the end buttons instead:
            // they collapse to 0 when there is nothing left to distribute.
            justifyContent: 'flex-start',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
            '& > button': { flex: '0 0 auto' },
            '& > button:first-of-type': { marginLeft: 'auto' },
            '& > button:last-of-type': { marginRight: 'auto' },
          }}
        >
          {currentWeekDates.map((date) => {
            const dateStr = formatDateForAPI(date);
            const hasSlots = availabilityData[dateStr]?.length > 0;
            const isPast = date < today;
            const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString();
            const isDisabled = isPast || !hasSlots;

            return (
              <button
                key={date.toDateString()}
                onClick={() => !isDisabled && handleDateClick(date, new Event('click'))}
                disabled={isDisabled}
                data-selected={isSelected ? 'true' : 'false'}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  minWidth: '60px',
                  padding: '8px 4px',
                  borderRadius: '8px',
                  textTransform: 'none',
                  color: isDisabled ? '#ccc' : 'black',
                  fontWeight: isSelected ? 'bold' : 'normal',
                  backgroundColor: isSelected ? fadedPrimaryColor : 'transparent',
                  border: isSelected ? `2px solid ${primaryColor}` : '1px solid #e0e0e0',
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  if (!isDisabled && !isSelected) {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isDisabled && !isSelected) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <span style={{ fontSize: '16px', fontWeight: isSelected ? 'bold' : 'normal', pointerEvents: 'none' }}>
                  {date.getDate()}
                </span>
                <span style={{ fontSize: '11px', marginTop: '2px', pointerEvents: 'none' }}>
                  {date.toLocaleDateString('en-US', { weekday: 'short' })}
                </span>
              </button>
            );
          })}
        </Box>

        <IconButton onClick={goToNextWeek} sx={{ color: 'black' }}>
          <ChevronRightIcon sx={{ fontSize: 24 }} />
        </IconButton>
      </Box>

      {/* Available Time Slots */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : selectedDate && availabilityData[formatDateForAPI(selectedDate)]?.length > 0 ? (
        <Box>
          <p style={{ marginBottom: '12px', color: '#666', fontSize: '14px' }}>
            Available time slots:
          </p>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
              gap: 1,
            }}
          >
            {availabilityData[formatDateForAPI(selectedDate)]?.map((slotTime, index) => {
              const isSelected = selectedTimeSlot === slotTime;

              return (
                <button
                  key={index}
                  onClick={() => handleTimeSlotClick(slotTime, new Event('click'))}
                  style={{
                    padding: '12px 16px',
                    borderRadius: '8px',
                    textTransform: 'none',
                    backgroundColor: isSelected ? primaryColor : 'transparent',
                    border: isSelected ? `1px solid ${primaryColor}` : '1px solid #e0e0e0',
                    color: isSelected ? 'white' : 'black',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      e.target.style.backgroundColor = '#f5f5f5';
                      e.target.style.borderColor = '#c0c0c0';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.borderColor = '#e0e0e0';
                    }
                  }}
                >
                  {formatTimeSlot(slotTime)}
                </button>
              );
            })}
          </Box>
        </Box>
      ) : selectedDate ? (
        <p style={{ color: '#666', fontSize: '14px', textAlign: 'center' }}>
          No available time slots for this date
        </p>
      ) : null}

      {/* CIN-7181: the location's pickup promise, below the time grid, for
          drop-offs only (waiter shows nothing; feature off unchanged). */}
      {enableWaiterDropoff && visitType === VISIT_TYPE_DROP_OFF && (
        <p className="pickup-promise" data-testid="pickup-promise">
          {selectedLocation?.dropoff?.pickup_promise || DEFAULT_PICKUP_PROMISE}
        </p>
      )}
    </div>
  );
};

export default DateSelection;